import { ApplicationInfo } from '@company/components/JobWorker/ApplicationInfo';
import CompanyWorkerHistory from '@company/components/JobWorker/CompanyWorkerHistory';
import { CalendarView } from '@company/components/JobWorker/CalendarView';
import {
  WorkerDetails,
  WorkerNotes,
  WorkerResume,
} from '@company/components/WorkerParts';
import { WorkerReviews } from '@company/components/WorkerReviews';
import {
  WorkerModalTabConfig,
  WorkerModalTabsEnum,
} from '@company/types/worker-modal.type';
import { IWorkerAssignment } from '@company/types/workerAssignment';
import {
  getWorkerActivityForTabs,
  getWorkerTagsForTabs,
} from '@company/utils/worker-modal-helper';
import { ThError, ThLoading } from '@components/elements';
import {
  ActionIcon,
  Container,
  Group,
  Menu,
  Stack,
  Tabs,
  Text,
} from '@mantine/core';
import { ITag } from '@th-types/tag.type';
import { IWorkerJobData } from '@th-types/workerProfile.type';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { AiTwotoneCalendar } from 'react-icons/ai';
import { BiUser } from 'react-icons/bi';
import { CgNotes } from 'react-icons/cg';
import { FaRegStar } from 'react-icons/fa';
import { FiChevronDown } from 'react-icons/fi';
import { RiHistoryFill, RiSuitcaseLine } from 'react-icons/ri';
import WorkerShifts from './WorkerShifts';

interface WorkerModalTabsProps {
  workerData: IWorkerJobData | undefined;
  isLoading: boolean;
  error: Error | null;
  initialTab?: WorkerModalTabsEnum;
  showJobDetails?: boolean;
  jobId?: number;
}

export default function WorkerModalTabs({
  workerData,
  isLoading,
  error,
  initialTab = WorkerModalTabsEnum.PROFILE_DETAILS,
  showJobDetails = false,
  jobId,
}: WorkerModalTabsProps) {
  const [activeKey, setActiveKey] = useState<WorkerModalTabsEnum>(initialTab);
  const [notesCount, setNotesCount] = useState(0);

  const getTags = useCallback(
    (matchSkills?: boolean, hired?: boolean): ITag[] => {
      if (!workerData?.worker) return [];
      return getWorkerTagsForTabs(workerData.worker, matchSkills, hired);
    },
    [workerData?.worker]
  );

  const getWorkerActivity = useCallback((): IWorkerAssignment => {
    if (!workerData) return {} as IWorkerAssignment;
    return getWorkerActivityForTabs(workerData);
  }, [workerData]);

  const getThisJobComponent = useCallback(() => {
    if (!workerData) return null;

    return (
      <Stack gap="md" flex={1}>
        <ApplicationInfo
          worker={workerData.worker}
          jobQuestionResponseList={workerData.jobQuestionList}
          workerAssignment={getWorkerActivity()}
          jobApplication={workerData.jobApplication}
        />
        <WorkerShifts
          worker={workerData.worker}
          jobId={jobId || workerData.jobAssignment?.job?.id}
          workerData={workerData}
        />
      </Stack>
    );
  }, [workerData, getWorkerActivity, jobId]);

  const tabs: WorkerModalTabConfig[] = useMemo(() => {
    if (!workerData) return [];

    const tabList = [
      {
        key: WorkerModalTabsEnum.PROFILE_DETAILS,
        icon: <BiUser size={20} />,
        label: 'Profile Details',
        component: (
          <WorkerDetails
            workerProfile={workerData}
            distance={workerData.jobAssignment?.distance}
            tags={getTags(
              workerData.jobAssignment?.matchSkills,
              workerData.jobAssignment?.hired
            )}
          />
        ),
      },
      {
        key: WorkerModalTabsEnum.REVIEWS,
        icon: <FaRegStar size={20} />,
        label: `Reviews (${workerData.comments?.length ?? 0})`,
        component: <WorkerReviews comments={workerData.comments} />,
      },
      {
        key: WorkerModalTabsEnum.RESUME,
        icon: <CgNotes size={20} />,
        label: 'Resume',
        component: <WorkerResume worker={workerData.worker} />,
      },
      {
        key: WorkerModalTabsEnum.CALENDAR,
        icon: <AiTwotoneCalendar size={20} />,
        label: 'Calendar',
        component: <CalendarView worker={workerData.worker} />,
      },
      {
        key: WorkerModalTabsEnum.HISTORY,
        icon: <RiHistoryFill size={20} />,
        label: 'History',
        component: (
          <CompanyWorkerHistory
            worker={workerData.worker}
            isActive={activeKey === WorkerModalTabsEnum.HISTORY}
          />
        ),
      },
      {
        key: WorkerModalTabsEnum.INTERNAL_NOTES,
        icon: <CgNotes size={20} />,
        label: `Internal Notes (${notesCount})`,
        component: (
          <WorkerNotes
            handleNotesCount={setNotesCount}
            worker={workerData.worker}
            isActive={activeKey === WorkerModalTabsEnum.INTERNAL_NOTES}
          />
        ),
      },
    ];

    // Show "This Job" tab if we're in a job context (showJobDetails is true and we have a jobId)
    // or if there's actual job assignment data
    if (showJobDetails && jobId) {
      tabList.unshift({
        key: WorkerModalTabsEnum.THIS_JOB,
        icon: <RiSuitcaseLine size={20} />,
        label: 'This Job',
        component: getThisJobComponent() || <div>No job data available</div>,
      });
    }

    return tabList;
  }, [
    workerData,
    activeKey,
    notesCount,
    getTags,
    getThisJobComponent,
    showJobDetails,
    jobId,
  ]);

  // Ensure activeKey is set to the first available tab if current activeKey doesn't exist
  useEffect(() => {
    if (tabs.length > 0) {
      const availableTabKeys = tabs.map((tab) => tab.key);
      const firstAvailableTab = tabs[0]?.key;

      // Update activeKey if it's not in the available tabs
      if (firstAvailableTab && !availableTabKeys.includes(activeKey)) {
        setActiveKey(firstAvailableTab);
      }
    }
  }, [tabs, activeKey]);

  // Handle loading and error states
  if (isLoading) {
    return (
      <Container size="lg">
        <ThLoading />
      </Container>
    );
  }
  if (error) return <ThError message="Failed to load worker data" />;
  if (!workerData) return <ThError message="No worker data found" />;

  const selectTab = (tabName: string | null) => {
    if (tabName) setActiveKey(tabName as WorkerModalTabsEnum);
  };

  const activeTab = tabs.find((tab) => tab.key === activeKey);

  if (isMobile) {
    return (
      <Stack gap="md">
        <Menu shadow="md" width={200}>
          <Menu.Target>
            <Group gap="xs" style={{ cursor: 'pointer', padding: '0.5rem' }}>
              <Text fw={700}>{activeTab?.label || 'Tab'}</Text>
              <ActionIcon variant="subtle" size="sm">
                <FiChevronDown size={16} />
              </ActionIcon>
            </Group>
          </Menu.Target>
          <Menu.Dropdown>
            {tabs.map((tab) => (
              <Menu.Item key={tab.key} onClick={() => selectTab(tab.key)}>
                {tab.label}
              </Menu.Item>
            ))}
          </Menu.Dropdown>
        </Menu>
        <div>{activeTab?.component}</div>
      </Stack>
    );
  }

  return (
    <Tabs value={activeKey} onChange={(value) => selectTab(value || '')}>
      <Tabs.List grow>
        {tabs.map((tab) => (
          <Tabs.Tab
            key={tab.key}
            value={tab.key}
            leftSection={tab.icon}
            style={{
              justifyContent: 'center',
            }}
            styles={{
              tabLabel: {
                flexGrow: 0,
              },
            }}
          >
            {tab.label}
          </Tabs.Tab>
        ))}
      </Tabs.List>
      {tabs.map((tab) => (
        <Tabs.Panel key={tab.key} value={tab.key} pt="md">
          {tab.component}
        </Tabs.Panel>
      ))}
    </Tabs>
  );
}
